import { type ReactNode, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { explainMetric } from '../../lib/explain';

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  prefix?: string;
  suffix?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; percent: number };
  sparkline?: number[];
  sparkData?: number[];
  accent?: 'default' | 'primary' | 'success' | 'warn' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  /** Enable the "Why?" explainer — pass the raw numeric values for the API */
  explainData?: { metric: string; current: number; prior?: number; period?: string; unit?: string };
  /** Plain-English one-liner explaining what this metric means (hover tooltip). */
  hint?: string;
}

interface AccentColors { stroke: string; fill: string; glow: string; }

const ACCENT_MAP: Record<string, AccentColors> = {
  default: { stroke: 'var(--primary)', fill: 'var(--primary)', glow: '0 0 20px rgba(155, 58, 248,0.15)' },
  primary: { stroke: 'var(--primary)', fill: 'var(--primary)', glow: '0 0 20px rgba(155, 58, 248,0.15)' },
  success: { stroke: 'var(--success)', fill: 'var(--success)', glow: '0 0 20px rgba(79,166,120,0.15)' },
  warn: { stroke: 'var(--warn)', fill: 'var(--warn)', glow: '0 0 20px rgba(201,150,87,0.15)' },
  danger: { stroke: 'var(--danger)', fill: 'var(--danger)', glow: '0 0 20px rgba(196,102,89,0.15)' },
};

function getAccent(key: string): AccentColors { return ACCENT_MAP[key] ?? ACCENT_MAP['default']!; }

function TrendIndicator({ direction, percent }: { direction: 'up' | 'down' | 'flat'; percent: number }) {
  const color = direction === 'up' ? 'text-success' : direction === 'down' ? 'text-danger' : 'text-ink-dim';
  const arrow = direction === 'up' ? (
    <svg aria-hidden="true" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8V2M2.5 4.5L5 2l2.5 2.5" /></svg>
  ) : direction === 'down' ? (
    <svg aria-hidden="true" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 2v6M2.5 5.5L5 8l2.5-2.5" /></svg>
  ) : (
    <svg aria-hidden="true" width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 5h6" /></svg>
  );
  return <span className={clsx('inline-flex items-center gap-0.5 font-mono text-2xs font-medium tabular-nums', color)}>{arrow}{percent.toFixed(1)}%</span>;
}

function Sparkline({ data, accent }: { data: number[]; accent: string }) {
  const colors = getAccent(accent);
  const chartData = data.map((v, i) => ({ i, v }));
  const gradientId = `metric-spark-${accent}-${data.length}`;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 2, right: 1, bottom: 0, left: 1 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.fill} stopOpacity={0.3} />
            <stop offset="60%" stopColor={colors.fill} stopOpacity={0.06} />
            <stop offset="100%" stopColor={colors.fill} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={colors.stroke} strokeWidth={1.5} fill={`url(#${gradientId})`} dot={false} isAnimationActive animationDuration={800} animationEasing="ease-out" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const SIZE_CONFIG = {
  sm: { wrapper: 'px-3 py-2', label: 'text-2xs', value: 'text-base', prefix: 'text-xs', suffix: 'text-2xs', spark: 'h-6 w-12' },
  md: { wrapper: 'px-4 py-3.5', label: 'text-2xs', value: 'text-xl', prefix: 'text-sm', suffix: 'text-2xs', spark: 'h-8 w-16' },
  lg: { wrapper: 'px-5 py-5', label: 'text-xs', value: 'text-3xl', prefix: 'text-base', suffix: 'text-sm', spark: 'h-10 w-20' },
} as const;

function WhyExplainer({ data }: { data: NonNullable<MetricCardProps['explainData']> }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<string | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  async function fetchExplanation(e: React.MouseEvent) {
    e.stopPropagation();
    if (explanation) {
      if (open) { setOpen(false); return; }
      // Reposition and reopen
      updatePos();
      setOpen(true);
      return;
    }
    updatePos();
    setOpen(true);
    setLoading(true);
    try {
      // Routes through explainMetric so the demo workspace returns a specific,
      // made-up explanation (correct direction, instant) instead of a generic
      // live-LLM round-trip; real tenants still hit /v1/explain/metric.
      const res = await explainMetric({
        metric: data.metric,
        current: data.current,
        prior: data.prior,
        period: data.period ?? 'this week vs last week',
        unit: data.unit,
      });
      setExplanation(res.explanation);
      setConfidence(res.confidence);
    } catch {
      setExplanation('Unable to generate explanation right now.');
      setConfidence('low');
    } finally {
      setLoading(false);
    }
  }

  function updatePos() {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: Math.max(8, rect.left) });
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={fetchExplanation}
        className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-ink-dim hover:text-primary hover:bg-primary/5 transition-colors"
        title="Why did this metric change?"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        Why?
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-80" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div
            className="fixed z-81 w-80 rounded-xl border border-border bg-surface-0 p-4 shadow-2xl animate-fade-in"
            style={{ top: pos.top, left: pos.left, maxWidth: 'calc(100vw - 16px)' }}
            onClick={e => e.stopPropagation()}
          >
            {loading ? (
              <div className="flex items-center gap-2.5 text-xs text-ink-dim">
                <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                Waypoint is thinking...
              </div>
            ) : (
              <>
                <p className="text-xs text-ink leading-relaxed">{explanation}</p>
                {confidence && (
                  <div className="mt-3 flex items-center justify-between border-t border-border pt-2">
                    <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium', confidence === 'high' ? 'bg-success/10 text-success' : confidence === 'med' ? 'bg-warn/10 text-warn' : 'bg-ink-dim/10 text-ink-dim')}>
                      {confidence} confidence
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="text-2xs text-ink-dim hover:text-ink transition-colors">Dismiss</button>
                  </div>
                )}
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export function MetricCard({ label, value, prefix, suffix, trend, sparkline, sparkData, accent = 'default', size = 'md', onClick, explainData, hint }: MetricCardProps) {
  const resolvedSparkline = sparkline ?? sparkData;
  const colors = getAccent(accent);
  const s = SIZE_CONFIG[size];
  return (
    <div
      className={clsx('group relative overflow-hidden rounded-xl border border-border bg-surface-1 card-highlight transition-all duration-300 animate-fade-in hover:-translate-y-0.5 hover:shadow-md hover:border-border-strong', onClick && 'cursor-pointer active:scale-[0.98]', s.wrapper)}
      style={{ boxShadow: colors.glow }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div className="absolute inset-x-0 top-0 h-[2px] opacity-60 transition-opacity duration-300 group-hover:opacity-100" style={{ background: `linear-gradient(90deg, transparent 0%, ${colors.stroke} 50%, transparent 100%)` }} />
      <div className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" style={{ background: colors.fill }} />
      <div className="pointer-events-none absolute inset-0 rounded-xl shadow-none transition-shadow duration-300 group-hover:shadow-lift" />
      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={clsx('mb-1 flex items-center gap-1.5', s.label)}>
            <span className="font-semibold uppercase tracking-wide text-ink-dim" title={hint}>{label}</span>
            {hint && (
              <span
                title={hint}
                aria-label={`What does ${label} mean?`}
                className="cursor-help select-none text-[10px] leading-none text-ink-dim/60 transition-colors hover:text-ink-muted"
                onClick={(e) => e.stopPropagation()}
              >
                ⓘ
              </span>
            )}
            {explainData && <WhyExplainer data={explainData} />}
          </div>
          <div className="flex items-baseline gap-1">
            {prefix && <span className={clsx('font-medium', s.prefix)} style={{ color: colors.stroke, opacity: 0.6 }}>{prefix}</span>}
            <span className={clsx('font-mono font-semibold tabular-nums leading-none tracking-tight text-ink transition-colors duration-200 group-hover:text-primary-bright', s.value)}>{value}</span>
            {suffix && <span className={clsx('font-medium', s.suffix)} style={{ color: colors.stroke, opacity: 0.6 }}>{suffix}</span>}
          </div>
          {trend && <div className="mt-1.5"><TrendIndicator direction={trend.direction} percent={trend.percent} /></div>}
        </div>
        {resolvedSparkline && resolvedSparkline.length > 1 && (
          <div className={clsx('shrink-0 opacity-60 transition-all duration-300 group-hover:opacity-100 group-hover:scale-105', s.spark)}>
            <Sparkline data={resolvedSparkline} accent={accent} />
          </div>
        )}
      </div>
    </div>
  );
}
